// src/oura/components/docs/threads/model.ts
//
// What the doc drawer's thread work shares (Docs phase 2, lane 4: 2.10 to
// 2.15): which threads Resolve N agreed takes, who wrote a post and how it
// arrived, which posts are Oura's own to edit, and the reads behind the
// approval record, the filed PDFs and the AI summary. Every read here is
// keyed null unless the doc is writable (`canWrite`, which reads
// DOCS_WRITE_ON), so a build missing its switch makes none of these calls.

import { useEffect, useRef, useState } from "react";
import {
    getApprovalRecord,
    getDocSummary,
    listApprovedReports,
    makeDocSummary,
    type AnswerVia,
    type ApprovalRecord,
    type ApprovedReport,
    type DocSummary,
} from "@service/aeo/clientDocsWrite";
import { opensWithAgreement, resolveCarriesAgreement, type ClientWord } from "@service/aeo/clientDocsRules";
import { docsFailure, type DocAuthor, type DocPost, type DocThread, type DocsFailure, type DocumentDetail, type StaffDocument } from "@service/aeo/clientDocs";
import { listTaskApprovals, type ApprovalOverride } from "@service/aeo/clientReview";
import type { DrawerConfirm, DrawerStatus } from "@oura/ui";
import { docDate, docLateText, useDocsLoad, type DocsLoad } from "@oura/docs";
import { houseVersion, isThreadView, type ThreadView } from "../write/model";

/* ------------------------------------------------------------------------- *
 * Threads
 * ------------------------------------------------------------------------- */

/** The thread view's mode in the doc sheet: "thread:{threadId}". */
export const threadView = (threadId: string): ThreadView => `thread:${threadId}`;

export function threadIdOf(mode: string): string | null {
    return isThreadView(mode) ? mode.slice("thread:".length) : null;
}

/** A question's number: its number in the doc's list, else its place among the doc's questions. */
export function questionNumber(t: DocThread, threads: readonly DocThread[]): number | null {
    if (t.kind !== "question") return null;
    if (t.number) return t.number;
    const at = threads.filter((x) => x.kind === "question").indexOf(t);
    return at < 0 ? null : at + 1;
}

/** "Question 2", "Raised by the client", "Our note", "Placeholder". */
export function threadTitle(t: DocThread, threads: readonly DocThread[]): string {
    if (t.kind === "question") {
        const n = questionNumber(t, threads);
        return n ? `Question ${n}` : "Question";
    }
    if (t.kind === "raised_by_client") return "Raised by the client";
    if (t.kind === "placeholder") return "Placeholder";
    return "Our note";
}

/**
 * A thread's text without its "[S2-MK]" prefix (the code shows as a chip). On Oura's own
 * questions it also drops the "Q3 " (or "3. ") they open with, as the row says the number;
 * a client's comment keeps every word it was written with.
 */
export function withoutCode(text: string, kind?: DocThread["kind"]): string {
    const bare = text.replace(/^\s*\[[^\]]*\]\s*/, "");
    return kind === "question" ? bare.replace(/^(?:Q\d+\s+|\d+\.\s+)/, "") : bare;
}

/** The threads that need us, in the doc drawer's order: raised by the client first, then questions. */
export function needsUsThreads(threads: readonly DocThread[]): DocThread[] {
    return [
        ...threads.filter((t) => t.kind === "raised_by_client" && t.state === "needs_us"),
        ...threads.filter((t) => t.kind === "question" && t.state === "needs_us"),
    ];
}

/** The client's last words on a thread (the opening, for one they raised), as the agreement rule reads them. */
export function lastClientWord(t: DocThread): ClientWord | null {
    let word: ClientWord | null = t.openedBy.side === "client" ? { text: t.text } : null;
    for (const p of t.posts) {
        if (p.author.side === "client" && p.action === "reply" && p.text.trim()) word = { text: p.text, answer: p.answer ?? null };
    }
    return word;
}

/**
 * A bare yes: the agreement word, then at most a thank-you. "AGREED",
 * "Yes.", "Approved, thank you". "Yes, those 3. Add kueh pie tee as a 4th"
 * agrees but asks for more, so it is not bare.
 */
const BARE_AGREEMENT_RE = /^\s*(yes|yep|agreed?|approved?|ok(ay)?|confirmed?|looks good|go ahead|all good)[\s.,!]*((thanks?|thank you|please)[\s.,!]*)*$/i;

/** Whether the rule reads an agreement from the client's free words, not from an answer kind. */
function agreesInWords(t: DocThread, word: ClientWord | null): word is ClientWord {
    const kind = t.questionKind ?? null;
    return Boolean(word && !word.answer && kind !== "choice" && kind !== "text" && kind !== "file");
}

/** An open question whose client's last word agrees, whatever else it says. */
function agreesAtAll(t: DocThread): boolean {
    return t.kind === "question" && !t.resolved && resolveCarriesAgreement("us", lastClientWord(t), t.questionKind ?? null);
}

/**
 * Opens with a yes but says more ("Yes, and add a 4th dish"): a person reads
 * it, never Resolve N agreed. Read from the opening words, not from the
 * agreement rule, because the rule does not count a yes followed by a change
 * as agreement at all ("Yes, but change..."), and those are the ones this
 * amber tag is for. Resolving one of those still asks "Resolve anyway?".
 */
export function clientAgreedWithChange(t: DocThread): boolean {
    if (t.kind !== "question" || t.resolved) return false;
    const word = lastClientWord(t);
    return agreesInWords(t, word) && opensWithAgreement(word.text) && !BARE_AGREEMENT_RE.test(word.text);
}

/** An open question whose client's last word is a bare yes: Resolve takes it in one click, and Resolve N agreed takes them all. */
export function clientAgreed(t: DocThread): boolean {
    return agreesAtAll(t) && !clientAgreedWithChange(t);
}

export function agreedThreads(threads: readonly DocThread[]): DocThread[] {
    return threads.filter(clientAgreed);
}

/** The thread's state once, for the thread view's head. */
export function threadStatus(t: DocThread): DrawerStatus {
    if (t.resolved) {
        const day = docDate(t.resolvedAt);
        if (t.resolvedAgreed === false) return { tone: "amber", label: "Resolved without their yes", clock: day ? { text: day } : null };
        return { tone: "green", label: t.kind === "question" ? "Approved" : "Resolved", clock: day ? { text: day } : null };
    }
    switch (t.state) {
        case "needs_us":
            return { tone: "blue", label: "Needs us" };
        case "late": {
            const late = docLateText({ late: true, lateWorkingDays: t.lateWorkingDays, replyBy: null });
            return { tone: "amber", label: "Waiting on client", clock: late ? { text: late, late: true } : null };
        }
        case "waiting_on_client":
            return { tone: "amber", label: "Waiting on client" };
        default:
            // No state: a thread the client raised that we answered, or one of ours that asks nothing.
            if (t.kind === "raised_by_client") return { tone: "gray", label: "We replied" };
            return { tone: "gray", label: t.kind === "placeholder" ? "Asks nothing" : "Not counted" };
    }
}

/* ------------------------------------------------------------------------- *
 * Posts
 * ------------------------------------------------------------------------- */

/** How a post on the client's behalf arrived, after their name: "in Oura", "by email", "on WhatsApp", "on a call". */
export const VIA_WORDS: Record<AnswerVia | "doc", string> = {
    oura: "in Oura",
    doc: "in the doc",
    email: "by email",
    whatsapp: "on WhatsApp",
    call: "on a call",
};

/** "Siew Ling", or "ourteam (no-reply@)" for the old posting account. */
export function authorWord(a: DocAuthor): string {
    return a.account === "no_reply" ? `${a.name} (no-reply@)` : a.name;
}

/** A post's "who" and "where" for its quote: the client's words with how they came, our replies with who signed them. */
export function postWords(p: DocPost): { who: string; where: string; at: string } {
    if (p.author.onBehalfOf) {
        const how = VIA_WORDS[p.via ?? "oura"];
        return { who: p.author.onBehalfOf, where: p.loggedBy ? `${how}, logged by ${p.loggedBy}` : how, at: p.saidAt ?? p.at };
    }
    // Every post of ours lives in the doc, wherever it was written: one label for all of them.
    const where = "in the doc";
    const who = p.signedBy ?? authorWord(p.author);
    return { who, where: p.editedAt ? `${where}, edited` : where, at: p.at };
}

/** The opening comment's post id, as the edit and delete routes take it. */
export const openingPostId = (t: DocThread) => `${t.id}:open`;

/** A post Oura wrote as oura@ (never one it wrote for the client, never anyone else's). Only these can be edited or deleted. */
export function isOurPost(p: Pick<DocPost, "author" | "action" | "text">): boolean {
    return p.author.account === "oura" && !p.author.onBehalfOf && p.action === "reply" && Boolean(p.text.trim());
}

/** The opening comment is Oura's own. */
export function isOurOpening(t: DocThread): boolean {
    return t.openedBy.account === "oura" && !t.openedBy.onBehalfOf;
}

/* ------------------------------------------------------------------------- *
 * Reads: the approval record, the filed PDFs, the summary
 * ------------------------------------------------------------------------- */

/** B23: the approval record for one doc. Null key while the doc is not writable. */
export function useApprovalRecord(documentId: string | null): DocsLoad<ApprovalRecord> {
    return useDocsLoad<ApprovalRecord>(documentId ? `approval-record:${documentId}` : null, () => getApprovalRecord(documentId ?? ""));
}

/** B24: the approved PDFs filed for a client, newest first. */
export function useApprovedReports(businessId: string | null): DocsLoad<ApprovedReport[]> {
    return useDocsLoad<ApprovedReport[]>(businessId ? `approved-reports:${businessId}` : null, () => listApprovedReports(businessId ?? ""));
}

/**
 * H20a: the client's yes a named person recorded outside the doc, on a key
 * whose code this doc covers ("B1:page_copy" on a doc coded B1). Read from the
 * task-approvals route the packs use; overrides with no reason (the packs' own,
 * the automatic ones) are left out.
 */
export function useRecordedYes(businessId: string | null, codes: readonly string[]): ApprovalOverride[] {
    const load = useDocsLoad<ApprovalOverride[]>(businessId ? `recorded-yes:${businessId}` : null, async () => {
        try {
            return { ok: true as const, data: await listTaskApprovals(businessId ?? "") };
        } catch {
            return docsFailure("network");
        }
    });
    return (load.data ?? [])
        .filter((o) => o.source === "staff" && o.requirement === "not_required" && Boolean(o.whereClientSaidYes) && codes.includes(o.taskId.split(":")[0]))
        .sort((a, b) => (b.setAt ?? "").localeCompare(a.setAt ?? ""));
}

/**
 * The PDF filed for the doc as it stands: the backend's `current` (#4874: no
 * versions, so "filed" is the latest filing with nothing new in the doc since).
 * An older backend sends no `current` and moved the doc on when it filed, so
 * there the filing whose `nextVersion` is the doc's version now is the one.
 */
export function filedForVersion(reports: readonly ApprovedReport[] | null, doc: Pick<StaffDocument, "id" | "version">): ApprovedReport | null {
    const mine = (reports ?? []).filter((r) => r.documentId === doc.id);
    if (mine.some((r) => r.current !== undefined)) return mine.find((r) => r.current) ?? null;
    const v = houseVersion(doc.version);
    return mine.find((r) => r.nextVersion && houseVersion(r.nextVersion) === v) ?? null;
}

export interface SummaryState {
    status: "loading" | "ready" | "error";
    summary: DocSummary | null;
    error: DocsFailure | null;
    /** "Make a new summary": asks first, since a new one is a paid model call (#4314). */
    remake?: () => void;
    /** A new one is being made on request. */
    remaking?: boolean;
    /** The drawer foot's question before a new summary is paid for; null when not asked. */
    confirm?: DrawerConfirm | null;
}

/** "$0.26 of $20.00 this month", or "nothing yet of $20.00 this month" before the first measured run. */
export function summarySpendText(cap: { monthCapUsd: number; spentUsd: number | null } | null | undefined): string | null {
    if (!cap) return null;
    const spent = cap.spentUsd === null ? "nothing yet" : `$${cap.spentUsd.toFixed(2)}`;
    return `${spent} of $${cap.monthCapUsd.toFixed(2)} this month`;
}

/**
 * B25: the AI summary, made only when staff open a doc that changed since
 * the last one. It runs once per opening: a doc that changes while it is open
 * keeps its summary (marked older than the doc) until the next opening, so
 * nothing is spent in the background. Never a source for a state, a count or
 * a date: nothing reads it but its own panel.
 */
export function useDocSummaryOnOpen(documentId: string | null, detail: DocumentDetail | null): SummaryState {
    const [state, setState] = useState<SummaryState & { id: string | null }>({ id: documentId, status: "loading", summary: null, error: null });
    const [remaking, setRemaking] = useState(false);
    /** "Make a new summary" was clicked: the foot asks before the paid call, for this doc only. */
    const [asking, setAsking] = useState<string | null>(null);
    const ran = useRef<string | null>(null);
    const ready = Boolean(detail && documentId && detail.id === documentId);
    // Read at the opening only: a change while the doc is open never re-runs it.
    const modifiedRef = useRef<string | null>(null);
    modifiedRef.current = detail?.modifiedAt ?? null;

    useEffect(() => {
        if (!documentId || !ready || ran.current === documentId) return;
        ran.current = documentId;
        let alive = true;
        const id = documentId;
        const modifiedAt = modifiedRef.current;
        void (async () => {
            const have = await getDocSummary(id);
            if (!alive) return;
            if (have.ok && have.data && have.data.madeFrom && have.data.madeFrom === modifiedAt) {
                setState({ id, status: "ready", summary: have.data, error: null });
                return;
            }
            const made = await makeDocSummary(id);
            if (!alive) return;
            if (made.ok) setState({ id, status: "ready", summary: made.data, error: null });
            // A refusal (the cap) keeps the last summary, if there is one, beside the reason.
            else setState({ id, status: have.ok && have.data ? "ready" : "error", summary: have.ok ? have.data : null, error: made });
        })();
        return () => {
            alive = false;
            // Unmounted or moved to another doc before it came back: the next opening runs it again.
            if (ran.current === id) ran.current = null;
        };
    }, [documentId, ready]);

    const makeFresh = () => {
        if (!documentId || remaking) return;
        const id = documentId;
        setRemaking(true);
        // fresh: a new one even when the doc is unchanged, which the plain call answers with the stored one (#6045)
        void makeDocSummary(id, { fresh: true }).then((made) => {
            setRemaking(false);
            setAsking(null);
            setState((prev) => (prev.id !== id ? prev : made.ok ? { id, status: "ready", summary: made.data, error: null } : { ...prev, error: made }));
        });
    };
    const remake = () => {
        if (documentId && !remaking) setAsking(documentId);
    };

    if (state.id !== documentId) return { status: "loading", summary: null, error: null };
    const spend = summarySpendText(state.summary?.cap);
    const confirm: DrawerConfirm | null =
        asking === documentId
            ? {
                  text: `Make a new AI summary from the doc as it stands? It is a paid AI call and counts against this month's summary cap${spend ? ` (${spend})` : ""}.`,
                  verb: "Make it",
                  busy: remaking,
                  onConfirm: makeFresh,
                  onCancel: () => setAsking(null),
              }
            : null;
    return { status: state.status, summary: state.summary, error: state.error, remake, remaking, confirm };
}
